<template>
  <div class="shops-container">
    <!-- (1) PageHeader -->
    <ds-page-header :title="$t('shops.page.title')" :subtitle="$t('shops.page.subtitle')" />

    <!-- (2) SearchPanel -->
    <ds-search-panel>
      <el-row :gutter="16">
        <el-col :xs="24" :sm="12" :md="8" :lg="8">
          <el-input v-model.trim="queryParams.roomId" clearable class="w-full"
            :placeholder="$t('shops.shopIdPlaceholder')" @keyup.enter.native="handleQuery" />
        </el-col>
        <el-col :xs="24" :sm="12" :md="8" :lg="8">
          <el-input v-model.trim="queryParams.roomNum" clearable class="w-full"
            :placeholder="$t('shops.shopNumberPlaceholder')" @keyup.enter.native="handleQuery" />
        </el-col>
        <el-col :xs="24" :sm="12" :md="8" :lg="8">
          <el-select v-model="queryParams.state" clearable class="w-full"
            :placeholder="$t('shops.statePlaceholder')">
            <el-option :label="$t('shops.rented')" value="2006" />
            <el-option :label="$t('shops.sold')" value="2007" />
            <el-option :label="$t('shops.idle')" value="2008" />
          </el-select>
        </el-col>
      </el-row>

      <template #actions>
        <el-button type="primary" icon="el-icon-search" @click="handleQuery">
          {{ $t('common.search') }}
        </el-button>
        <el-button icon="el-icon-refresh-left" @click="handleReset">
          {{ $t('common.reset') }}
        </el-button>
      </template>
    </ds-search-panel>

    <!-- (3)(4)(6) Toolbar + DataTable + PaginationBar -->
    <div class="ds-panel">
      <ds-toolbar :total="pagination.total" :density="density" :show-column-setting="false" :show-refresh="true"
        @update:density="density = $event" @refresh="loadData">
        <template #left>
          <span class="ds-panel__title">{{ $t('shops.shopInfo') }}</span>
        </template>
        <template #right>
          <el-button v-if="hasPrivilege('502020082453087940')" type="primary" size="mini" icon="el-icon-plus"
            @click="openAddDialog">
            {{ $t('shops.addShop') }}
          </el-button>
        </template>
      </ds-toolbar>

      <ds-data-table :data="shopsData" :loading="loading" :error="hasError" :error-message="errorMessage"
        :density="density" border :empty-description="$t('shops.page.empty')" @retry="loadData">
        <el-table-column prop="roomId" :label="$t('shops.shopId')" align="center" width="130">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.roomId || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column :label="$t('shops.shopNumber')" align="center" width="120">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.floorNum }}-{{ scope.row.roomNum }}</span>
          </template>
        </el-table-column>
        <el-table-column :label="$t('shops.floor')" align="center" width="90">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.layer }}{{ $t('shops.layerUnit') }}</span>
          </template>
        </el-table-column>
        <el-table-column :label="$t('shops.owner')" align="center" min-width="130" show-overflow-tooltip>
          <template slot-scope="scope">
            <router-link v-if="scope.row.ownerName" :to="`/views/owner/ownerDetail?ownerId=${scope.row.ownerId}`"
              target="_blank">
              {{ scope.row.ownerName }}
            </router-link>
            <span v-else class="ds-cell-sub">-</span>
          </template>
        </el-table-column>
        <el-table-column prop="link" :label="$t('shops.contactNumber')" align="center" width="120">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.link || '-' }}</span>
          </template>
        </el-table-column>
        <!-- 面积：建筑面积/室内面积 两行主次分明 -->
        <el-table-column :label="$t('shops.area')" align="center" width="130">
          <template slot-scope="scope">
            <div class="ds-num">{{ scope.row.builtUpArea }}{{ $t('shops.squareMeter') }}</div>
            <div class="ds-num ds-cell-sub">{{ scope.row.roomArea }}{{ $t('shops.squareMeter') }}</div>
          </template>
        </el-table-column>
        <!-- 起租/截租时间：两行 -->
        <el-table-column :label="$t('shops.leasePeriod')" align="center" width="150">
          <template slot-scope="scope">
            <div class="ds-num">{{ scope.row.startTime || '-' }}</div>
            <div class="ds-num ds-cell-sub">{{ scope.row.endTime || '-' }}</div>
          </template>
        </el-table-column>
        <el-table-column prop="roomRent" :label="$t('shops.rent')" align="right" width="110">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.roomRent != null ? scope.row.roomRent : '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="feeCoefficient" :label="$t('shops.feeCoefficient')" align="right" width="110">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.feeCoefficient != null ? scope.row.feeCoefficient : '-' }}</span>
          </template>
        </el-table-column>
        <!-- 状态 Tag：颜色仅作辅助，始终带文字（WCAG 1.4.1） -->
        <el-table-column prop="stateName" :label="$t('shops.state')" align="center" width="110">
          <template slot-scope="scope">
            <ds-status-tag :type="resolveState(scope.row)" :label="scope.row.stateName || '-'" />
          </template>
        </el-table-column>
        <el-table-column v-for="(item, index) in listColumns" :key="index" :label="item" align="center">
          <template slot-scope="scope">
            {{ scope.row.listValues[index] }}
          </template>
        </el-table-column>
        <el-table-column :label="$t('shops.operation')" align="center" width="230" fixed="right">
          <template slot-scope="scope">
            <el-button v-if="hasPrivilege('502020082493857941') && scope.row.state === '2008'" size="mini" type="text"
              @click="openRentDialog(scope.row)">
              {{ $t('shops.rentAction') }}
            </el-button>
            <el-button v-if="hasPrivilege('502020082493857941') && scope.row.state === '2008'" size="mini" type="text"
              @click="openSellDialog(scope.row)">
              {{ $t('shops.sellAction') }}
            </el-button>
            <el-button v-if="hasPrivilege('502020082493857941') && scope.row.state !== '2008'" size="mini" type="text"
              @click="openUnbind(scope.row)">
              {{ $t('shops.unbind') }}
            </el-button>
            <el-button v-if="hasPrivilege('502020082493857941')" size="mini" type="text" @click="openEditDialog(scope.row)">
              {{ $t('shops.edit') }}
            </el-button>
            <el-button v-if="hasPrivilege('502020082417457942')" size="mini" type="text" class="ds-btn-danger"
              @click="openDeleteDialog(scope.row)">
              {{ $t('shops.delete') }}
            </el-button>
          </template>
        </el-table-column>
      </ds-data-table>

      <ds-pagination-bar :total="pagination.total" :page="pagination.current" :size="pagination.size"
        :page-sizes="[10, 20, 30, 50]" @size-change="handleSizeChange" @current-change="handleCurrentChange">
        <template #summary>
          <span class="ds-tip-text">{{ $t('shops.page.tip') }}</span>
        </template>
      </ds-pagination-bar>
    </div>

    <!-- 子组件 -->
    <add-shops ref="addDialog" @success="loadData" />
    <bind-owner-shops ref="bindDialog" @success="loadData" />
    <edit-shops ref="editDialog" @success="loadData" />
    <delete-room ref="deleteDialog" @handleRefreshRoom="loadData" />
  </div>
</template>

<script>
import { queryShops } from '@/api/room/shopsApi'
import AddShops from '@/components/room/addShops'
import BindOwnerShops from '@/components/room/bindOwnerShops'
import EditShops from '@/components/room/editShops'
import DeleteRoom from '@/components/room/deleteRoom'
import { resolveStatus } from '@/utils/statusMap'
import DsPageHeader from '@/components/ds/ds-page-header.vue'
import DsSearchPanel from '@/components/ds/ds-search-panel.vue'
import DsToolbar from '@/components/ds/ds-toolbar.vue'
import DsDataTable from '@/components/ds/ds-data-table.vue'
import DsStatusTag from '@/components/ds/ds-status-tag.vue'
import DsPaginationBar from '@/components/ds/ds-pagination-bar.vue'

export default {
  name: 'ShopsList',
  components: {
    AddShops,
    BindOwnerShops,
    EditShops,
    DeleteRoom,
    DsPageHeader,
    DsSearchPanel,
    DsToolbar,
    DsDataTable,
    DsStatusTag,
    DsPaginationBar
  },
  data() {
    return {
      loading: false,
      hasError: false,
      errorMessage: '',
      density: 'default',
      queryParams: {
        roomId: '',
        roomNum: '',
        state: '',
        roomType: '2020602',
        page: 1,
        row: 10
      },
      shopsData: [],
      listColumns: [],
      pagination: {
        current: 1,
        size: 10,
        total: 0
      }
    }
  },
  created() {
    this.loadData()
  },
  methods: {
    loadData() {
      this.loading = true
      this.hasError = false
      this.errorMessage = ''
      queryShops(this.queryParams)
        .then(res => {
          this.shopsData = Array.isArray(res.rooms) ? res.rooms : []
          this.pagination.total = res.total || 0
          this.dealShopsAttr(this.shopsData)
        })
        .catch(error => {
          console.error(error)
          this.hasError = true
          this.errorMessage = this.$t('shops.page.loadFailed')
          this.$message.error(this.$t('common.requestError'))
        })
        .finally(() => {
          this.loading = false
        })
    },
    handleQuery() {
      this.queryParams.page = 1
      this.pagination.current = 1
      this.loadData()
    },
    handleReset() {
      this.queryParams = {
        roomId: '',
        roomNum: '',
        state: '',
        roomType: '2020602',
        page: 1,
        row: 10
      }
      this.pagination.current = 1
      this.pagination.size = 10
      this.loadData()
    },
    handleSizeChange(size) {
      this.queryParams.row = size
      this.pagination.size = size
      this.pagination.current = this.queryParams.page
      this.loadData()
    },
    handleCurrentChange(page) {
      this.queryParams.page = page
      this.pagination.current = page
      this.loadData()
    },
    openAddDialog() {
      this.$refs.addDialog.open()
    },
    openRentDialog(row) {
      this.$refs.bindDialog.open(row, '2006')
    },
    openSellDialog(row) {
      this.$refs.bindDialog.open(row, '2007')
    },
    openEditDialog(row) {
      this.$refs.editDialog.open(row)
    },
    openDeleteDialog(row) {
      this.$refs.deleteDialog.open(row)
    },
    openUnbind(row) {
      this.$router.push(`/views/owner/deleteOwnerRoom?ownerId=${row.ownerId}`)
    },
    dealShopsAttr(shopss) {
      // 处理商铺属性列
      // this.listColumns = ['Attr1', 'Attr2'] // 实际应从API获取
      shopss.forEach(shop => {
        shop.listValues = [] // 实际应从API获取
      })
    },
    /**
     * 状态语义类型（房屋/商铺）：颜色仅作辅助，始终带文字。
     */
    resolveState(row) {
      if (!row) return 'muted'
      return resolveStatus(row.state, {
        domain: 'room',
        text: row.stateName || ''
      }).type
    }
  }
}
</script>

<style lang="scss" scoped>
.shops-container {
  padding: var(--ds-spacing-lg);
  background-color: var(--ds-gray-100);
  min-height: calc(100vh - 60px);
}

/* 表格 + 分页 组成的白色面板 */
.ds-panel {
  background: #fff;
  border: 1px solid var(--ds-gray-300);
  border-radius: var(--ds-radius-md);
  box-shadow: var(--ds-shadow-sm);
  overflow: hidden;

  &__title {
    font-size: var(--ds-font-size-lg);
    font-weight: 600;
    color: var(--ds-gray-800);
  }
}

/* 表格次要文字 */
.ds-cell-sub {
  font-size: var(--ds-font-size-xs);
  color: var(--ds-gray-500);
}

/* 分页条左侧提示文案 */
.ds-tip-text {
  font-size: var(--ds-font-size-xs);
  color: var(--ds-gray-500);
  line-height: 1.6;
}

/* 危险操作文字按钮 */
.ds-btn-danger {
  color: var(--ds-danger) !important;
}
</style>
